<template>
  <div class="pricing-page">
    <hr class="mt-0" />

    <!-- Filters -->
    <div class="row align-items-center mt-4 mb-4">
      <div class="col-auto">
        <label class="form-label fw-semibold mb-1">Cottage</label>
        <select
          class="form-select"
          style="min-width: 200px"
          v-model="selectedCottageId"
          :disabled="cottagesLoading"
        >
          <option value="">{{ cottagesLoading ? 'Loading...' : 'Select cottage' }}</option>
          <option v-for="c in cottages" :key="c._id" :value="c._id">
            {{ c.retreatDisplayName }} — {{ c.cottage_name }}
          </option>
        </select>
      </div>
      <div class="col-auto">
        <label class="form-label fw-semibold mb-1">Year</label>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="year--">←</button>
          <input type="text" class="form-control form-control-sm text-center" style="width: 80px" v-model.number="year" />
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="year++">→</button>
        </div>
      </div>
      <div class="col-auto">
        <label class="form-label fw-semibold mb-1">Month</label>
        <select
          class="form-select form-select-sm"
          style="min-width: 140px"
          v-model.number="selectedMonthIndex"
        >
          <option :value="-1">All months</option>
          <option v-for="(m, idx) in months" :key="idx" :value="idx">
            {{ m }}
          </option>
        </select>
      </div>
    </div>

    <template v-if="selectedCottage">
      <!-- Color code legend -->
      <div class="color-legend mb-3">
        <span class="legend-label fw-semibold me-2">Color code:</span>
        <span class="legend-item">
          <span class="legend-swatch weekend"></span>
          <span>Weekend</span>
        </span>
        <span class="legend-item">
          <span class="legend-swatch weekday"></span>
          <span>Weekday</span>
        </span>
        <span class="legend-item">
          <span class="legend-swatch special-day"></span>
          <span>Special day</span>
        </span>
        <span class="legend-item">
          <span class="legend-swatch custom-price"></span>
          <span>Custom price</span>
        </span>
      </div>

      <!-- Toggle bulk update + Update prices -->
      <div class="mb-3 d-flex justify-content-between align-items-center">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="showBulkUpdate = !showBulkUpdate"
        >
          {{ showBulkUpdate ? 'Hide' : 'Update prices by type' }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="!hasChanges"
          @click="openPreviewModal"
        >
          Update prices
          <span v-if="previewChanges.length" class="badge bg-light text-dark ms-1">{{ previewChanges.length }}</span>
        </button>
      </div>

      <!-- Bulk price update -->
      <div v-show="showBulkUpdate" class="bulk-update mb-4 p-3 border rounded bg-light">
        <p class="fw-semibold mb-2">Update prices by type</p>
        <div class="row g-3 align-items-end">
          <div class="col-auto">
            <label class="form-label small mb-1">Weekend price (Rs)</label>
            <div class="d-flex gap-2 align-items-center">
              <input
                type="text"
                class="form-control form-control-sm"
                style="width: 100px"
                v-model="bulkPrices.weekend"
                @input="allowNumbersOnly($event)"
                placeholder="—"
              />
              <button type="button" class="btn btn-sm btn-outline-primary" @click="applyBulkPrice('weekend')">
                Apply
              </button>
            </div>
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Weekday price (Rs)</label>
            <div class="d-flex gap-2 align-items-center">
              <input
                type="text"
                class="form-control form-control-sm"
                style="width: 100px"
                v-model="bulkPrices.weekday"
                @input="allowNumbersOnly($event)"
                placeholder="—"
              />
              <button type="button" class="btn btn-sm btn-outline-primary" @click="applyBulkPrice('weekday')">
                Apply
              </button>
            </div>
          </div>
          <div class="col-auto">
            <label class="form-label small mb-1">Special day price (Rs)</label>
            <div class="d-flex gap-2 align-items-center">
              <input
                type="text"
                class="form-control form-control-sm"
                style="width: 100px"
                v-model="bulkPrices.specialDay"
                @input="allowNumbersOnly($event)"
                placeholder="—"
              />
              <button type="button" class="btn btn-sm btn-outline-primary" @click="applyBulkPrice('specialDay')">
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Price grid: months (vertical) × dates (horizontal) -->
      <div v-if="cottagePricingLoading" class="text-muted py-4">Loading pricing...</div>
      <div v-else class="table-responsive">
        <table class="table table-bordered pricing-grid">
          <thead>
            <tr>
              <th class="month-col bg-light sticky-col">Month</th>
              <th v-for="d in 31" :key="d" class="date-col text-center">{{ d }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="i in visibleMonthIndices"
              :key="i"
            >
              <td class="month-col bg-light fw-semibold sticky-col">{{ months[i] }}</td>
              <td
                v-for="d in 31"
                :key="d"
                class="date-cell clickable"
                :title="d <= daysInMonth(i) ? cellTooltip(i, d) : ''"
                :class="d <= daysInMonth(i) ? getCellType(i, d) : ''"
                @click="d <= daysInMonth(i) && openCellPopup(i, d)"
              >
                <template v-if="d <= daysInMonth(i)">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="day-label">{{ dayAbbrev(i, d) }}</div>
                    <button
                      v-if="isCustomCell(i, d)"
                      type="button"
                      class="btn btn-sm btn-link p-0 text-danger cell-delete-btn"
                      title="Delete custom price"
                      @click.stop="deleteCellCustomPrice(i, d)"
                    >
                      ×
                    </button>
                  </div>
                  <div class="cell-price">{{ getCellPrice(i, d) || '—' }}</div>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Preview modal -->
      <div
        class="modal fade show"
        :class="{ show: showPreviewModal }"
        :style="{ display: showPreviewModal ? 'block' : 'none' }"
        tabindex="-1"
      >
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content p-3">
            <div class="modal-header border-0 pb-2">
              <h5 class="modal-title">Preview price changes</h5>
              <button type="button" class="btn-close" @click="closePreviewModal"></button>
            </div>
            <div class="modal-body">
              <p class="text-muted small mb-3">Review the changes before submitting.</p>
              <div class="table-responsive" style="max-height: 300px; overflow-y: auto">
                <table class="table table-sm table-bordered">
                  <thead class="table-light sticky-top">
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Previous price</th>
                      <th>New price</th>
                      <th style="width: 80px">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in previewChanges" :key="item.key">
                      <td>{{ item.dateLabel }}</td>
                      <td>{{ item.type }}</td>
                      <td>{{ item.oldPrice || '—' }}</td>
                      <td>{{ item.newPrice || '—' }}</td>
                      <td class="text-center">
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="removePreviewItem(item)"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p v-if="previewChanges.length === 0" class="text-muted small mb-0">No changes to preview.</p>
            </div>
            <div class="modal-footer border-0 pt-2">
              <button type="button" class="btn btn-secondary" @click="closePreviewModal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="submitPriceUpdates">Confirm & submit</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" v-if="showPreviewModal"></div>

      <!-- Cell edit popup -->
      <div
        class="modal fade show"
        :class="{ show: showCellPopup }"
        :style="{ display: showCellPopup ? 'block' : 'none' }"
        tabindex="-1"
      >
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content p-3">
            <div class="modal-header border-0 pb-2">
              <h5 class="modal-title">Edit price</h5>
              <button type="button" class="btn-close" @click="closeCellPopup"></button>
            </div>
            <div class="modal-body" v-if="popupCell">
              <p class="mb-2 fw-semibold">{{ popupDateLabel }}</p>
              <div class="mb-3">
                <label class="form-label small">Price (Rs)</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="popupPrice"
                  @input="allowNumbersOnly($event)"
                  placeholder="Enter price"
                />
              </div>
              <button
                v-if="isCustomCell(popupCell.monthIndex, popupCell.date)"
                type="button"
                class="btn btn-link text-danger p-0 small"
                @click="deletePopupCustomPrice"
              >
                Delete custom price
              </button>
            </div>
            <div class="modal-footer border-0 pt-2">
              <button type="button" class="btn btn-secondary" @click="closeCellPopup">Cancel</button>
              <button type="button" class="btn btn-primary" @click="saveCellPrice">Save</button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show" v-if="showCellPopup"></div>
    </template>
  </div>
</template>

<script>
export default {
  name: "RetreatPricing",
  data() {
    return {
      year: new Date().getFullYear(),
      months: [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
      ],
      daysInMonth: (m) => [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m],
      cottages: [],
      cottagesLoading: false,
      selectedCottageId: "",
      cottageDetails: null,
      cottagePricingLoading: false,
      specialDayList: [],
      cellData: {},
      lastSavedCellData: {},
      showCellPopup: false,
      showPreviewModal: false,
      popupCell: null,
      popupPrice: "",
      popupIsSpecialDay: false,
      showBulkUpdate: false,
      bulkPrices: { weekend: "", weekday: "", specialDay: "" },
      originalBulkPrices: { weekend: "", weekday: "", specialDay: "" },
      specialDayKeys: {},
      selectedMonthIndex: -1,
    };
  },
  computed: {
    hasChanges() {
      const curr = JSON.stringify(this.cellData);
      const saved = JSON.stringify(this.lastSavedCellData);
      return curr !== saved;
    },
    visibleMonthIndices() {
      if (this.selectedMonthIndex === -1) {
        return this.months.map((_, idx) => idx);
      }
      return [this.selectedMonthIndex];
    },
    previewChanges() {
      const changes = [];
      const allKeys = new Set([
        ...Object.keys(this.cellData),
        ...Object.keys(this.lastSavedCellData),
      ]);
      for (const key of allKeys) {
        const [mi, d] = key.split("-").map(Number);
        if (Number.isNaN(mi) || Number.isNaN(d) || d < 1 || d > this.daysInMonth(mi)) {
          continue;
        }

        const currCell = this.cellData[key];
        const prevCell = this.lastSavedCellData[key];
        const isCustomNow = !!currCell?.isCustom;
        const wasCustom = !!prevCell?.isCustom;
        const currPrice = currCell?.price ?? "";
        const prevPrice = prevCell?.price ?? "";

        if (wasCustom && !isCustomNow) {
          changes.push({
            key,
            dateLabel: `${this.months[mi]} ${d}, ${this.year}`,
            type: "Custom price",
            oldPrice: prevPrice,
            newPrice: "",
            action: "delete",
          });
          continue;
        }

        if (isCustomNow) {
          if (currPrice !== prevPrice) {
            changes.push({
              key,
              dateLabel: `${this.months[mi]} ${d}, ${this.year}`,
              type: "Custom price",
              oldPrice: prevPrice,
              newPrice: currPrice,
              action: "set",
            });
          }
          continue;
        }

        if (currPrice !== prevPrice) {
          const baseType = this.getBaseCellType(mi, d);
          const typeLabel =
            baseType === "weekend"
              ? "Weekend price"
              : baseType === "special-day"
              ? "Special day price"
              : "Weekday price";
          changes.push({
            key,
            dateLabel: `${this.months[mi]} ${d}, ${this.year}`,
            type: typeLabel,
            oldPrice: prevPrice,
            newPrice: currPrice,
            action: "base",
          });
        }
      }
      return changes.sort((a, b) => a.key.localeCompare(b.key));
    },
    popupDateLabel() {
      if (!this.popupCell) return "";
      const m = this.months[this.popupCell.monthIndex];
      return `${m} ${this.popupCell.date}, ${this.year}`;
    },
    selectedCottage() {
      return this.cottages.find((c) => c._id === this.selectedCottageId) || null;
    },
  },
  watch: {
    selectedCottageId() {
      this.fetchCottagePricing();
    },
    year: {
      handler() {
        this.fetchCottagePricing();
      },
      immediate: false,
    },
  },
  mounted() {
    this.fetchCottages();
  },
  methods: {
    fetchCottages() {
      this.cottagesLoading = true;
      this.$apiV1
        .get("/retreats",{
          params: {
            includeCottages: true
          }
        })
        .then((res) => {
          console.log(res.data);
          if (res.data.success && res.data.data) {
            const list = [];
            res.data.data.forEach((retreat) => {
              (retreat.cottages || []).forEach((c) => {
                console.log(c);
                list.push({
                  _id: c._id,
                  cottage_name: c.cottage_name,
                  retreat_id: retreat._id,
                  retreatDisplayName: retreat.display_name || "Retreat",
                });
              });
            });
            this.cottages = list;
          }
        })
        .finally(() => {
          this.cottagesLoading = false;
        });
    },
    fetchCottagePricing() {
      const cottage = this.selectedCottage;
      if (!cottage) {
        this.cottageDetails = null;
        this.cellData = {};
        this.lastSavedCellData = {};
        this.bulkPrices = { weekend: "", weekday: "", specialDay: "" };
        this.originalBulkPrices = { weekend: "", weekday: "", specialDay: "" };
        return;
      }
      this.cottagePricingLoading = true;
      this.$apiV1
        .get(`/farm-stay/retreat/${cottage.retreat_id}/cottage/${cottage._id}`, {
          params: { year: this.year },
        })
        .then((res) => {
          if (res.data.success && res.data.data) {
            const d = res.data.data;
            this.cottageDetails = d;
            const pricing = d.pricing || d;
            const weekdays = String(pricing.weekdays_price ?? d.weekdays_price ?? "");
            const weekends = String(pricing.weekends_price ?? d.weekends_price ?? "");
            const special = String(pricing.special_days_price ?? d.special_days_price ?? "");
            this.bulkPrices = { weekend: weekends, weekday: weekdays, specialDay: special };
            this.originalBulkPrices = {
              weekend: weekends,
              weekday: weekdays,
              specialDay: special,
            };
            this.cellData = {};
            this.lastSavedCellData = {};
            this.specialDayKeys = {};
            this.specialDayList = [];

            const specialDayArray = d.specialDay || d.special_days || [];
            specialDayArray.forEach((item) => {
              const formatted = item.formattedDate || item.formatted_date;
              if (!formatted) return;
              this.specialDayList.push(formatted);
              const { monthIndex: mi, day: dd } = this.parseApiDate(formatted);
              if (mi == null) return;
              const key = this.cellKey(mi, dd);
              this.specialDayKeys[key] = true;
            });

            const cottageCount = d.cottage_count ?? 0;
            for (let i = 0; i < 12; i++) {
              const maxD = this.daysInMonth(i);
              for (let date = 1; date <= maxD; date++) {
                const key = this.cellKey(i, date);
                const type = this.getCellType(i, date);
                const price =
                  type === "weekend"
                    ? weekends
                    : type === "special-day"
                    ? special
                    : weekdays;
                this.cellData[key] = { price, inventory: cottageCount };
              }
            }
            const customCalendar = d.cottagePriceCalendar || d.cottage_price_calendar || [];
            customCalendar.forEach((entry) => {
              const formatted =
                entry.formattedDate || entry.formatted_date || entry.date || entry.calendar_date;
              if (!formatted) return;
              const { monthIndex: mi, day: dd } = this.parseApiDate(formatted);
              if (mi == null) return;
              const key = this.cellKey(mi, dd);
              if (!this.cellData[key]) {
                this.cellData[key] = {
                  price: "",
                  inventory: cottageCount,
                };
              }
              const current = this.cellData[key];
              current.price = String(
                entry.price ??
                  entry.custom_price ??
                  current.price ??
                  ""
              );
              if (entry.inventory != null || entry.inventory_count != null) {
                current.inventory = entry.inventory ?? entry.inventory_count;
              }
              current.akey = entry.akey ?? entry.id ?? entry._id ?? null;
              current.isCustom = true;
            });
            this.lastSavedCellData = JSON.parse(JSON.stringify(this.cellData));
          }
        })
        .catch(() => {
          this.cottageDetails = null;
          this.cellData = {};
          this.lastSavedCellData = {};
          this.bulkPrices = { weekend: "", weekday: "", specialDay: "" };
          this.originalBulkPrices = { weekend: "", weekday: "", specialDay: "" };
          this.specialDayKeys = {};
          this.specialDayList = [];
        })
        .finally(() => {
          this.cottagePricingLoading = false;
        });
    },
    cellKey(monthIndex, date) {
      return `${monthIndex}-${date}`;
    },
    parseApiDate(dateStr) {
      const dt = new Date(dateStr);
      if (Number.isNaN(dt.getTime())) return { monthIndex: null, day: null };
      return {
        monthIndex: dt.getUTCMonth(),
        day: dt.getUTCDate(),
      };
    },
    getCellPrice(monthIndex, date) {
      return this.cellData[this.cellKey(monthIndex, date)]?.price ?? "";
    },
    getCellInventory(monthIndex, date) {
      return this.cellData[this.cellKey(monthIndex, date)]?.inventory ?? 0;
    },
    isCustomCell(monthIndex, date) {
      const key = this.cellKey(monthIndex, date);
      return !!this.cellData[key]?.isCustom;
    },
    cellTooltip(monthIndex, date) {
      const d = new Date(this.year, monthIndex, date);
      const weekdayShort = d.toLocaleString("default", { weekday: "short" });
      const day = String(d.getDate()).padStart(2, "0");
      const monthShort = d.toLocaleString("default", { month: "short" });
      const yearShort = String(d.getFullYear()).slice(-2);
      const typeRaw = this.getCellType(monthIndex, date).replace("-", " ");
      const type = typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1);
      const price = this.getCellPrice(monthIndex, date) || "—";
      return `${weekdayShort} ${day} ${monthShort} ${yearShort} • ${type} • Rs ${price}`;
    },
    openCellPopup(monthIndex, date) {
      this.popupCell = { monthIndex, date };
      this.popupPrice = this.getCellPrice(monthIndex, date);
      this.popupIsSpecialDay = this.isSpecialDay(monthIndex, date);
      this.showCellPopup = true;
    },
    closeCellPopup() {
      this.showCellPopup = false;
      this.popupCell = null;
      this.popupPrice = "";
      this.popupIsSpecialDay = false;
    },
    saveCellPrice() {
      if (!this.popupCell) return;
      const key = this.cellKey(this.popupCell.monthIndex, this.popupCell.date);
      if (!this.cellData[key]) {
        this.cellData[key] = { price: "", inventory: this.cottageDetails?.cottage_count ?? 0 };
      }
      this.cellData[key].price = this.popupPrice;
      this.cellData[key].isCustom = true;
      this.closeCellPopup();
    },
    dayAbbrev(monthIndex, date) {
      const d = new Date(this.year, monthIndex, date);
      return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
    },
    isWeekend(monthIndex, date) {
      const d = new Date(this.year, monthIndex, date);
      const day = d.getDay();
      return day === 0 || day === 6;
    },
    isSpecialDay(monthIndex, date) {
      return !!this.specialDayKeys[this.cellKey(monthIndex, date)];
    },
    getBaseCellType(monthIndex, date) {
      if (this.isSpecialDay(monthIndex, date)) return "special-day";
      return this.isWeekend(monthIndex, date) ? "weekend" : "weekday";
    },
    resetCellToBase(monthIndex, date) {
      const type = this.getBaseCellType(monthIndex, date);
      const weekdays = this.bulkPrices.weekday;
      const weekends = this.bulkPrices.weekend;
      const special = this.bulkPrices.specialDay;
      const price =
        type === "weekend"
          ? weekends
          : type === "special-day"
          ? special
          : weekdays;
      const inventory = this.cottageDetails?.cottage_count ?? this.getCellInventory(monthIndex, date);
      const key = this.cellKey(monthIndex, date);
      const baseCell = {
        price: price || "",
        inventory,
      };
      if (this.$set) {
        this.$set(this.cellData, key, baseCell);
      } else {
        this.cellData[key] = baseCell;
      }
    },
    getCellType(monthIndex, date) {
      const key = this.cellKey(monthIndex, date);
      if (this.cellData[key]?.isCustom) return "custom-price";
      if (this.isSpecialDay(monthIndex, date)) return "special-day";
      return this.isWeekend(monthIndex, date) ? "weekend" : "weekday";
    },
    applyBulkPrice(type) {
      const priceMap = {
        weekend: this.bulkPrices.weekend,
        weekday: this.bulkPrices.weekday,
        specialDay: this.bulkPrices.specialDay,
      };
      const price = priceMap[type];
      if (!price) return;
      for (let i = 0; i < 12; i++) {
        const maxD = this.daysInMonth(i);
        for (let d = 1; d <= maxD; d++) {
          const key = this.cellKey(i, d);
          const isCustom = !!this.cellData[key]?.isCustom;
          let match = false;
          if (type === "weekend") {
            match = this.isWeekend(i, d) && !this.isSpecialDay(i, d) && !isCustom;
          } else if (type === "weekday") {
            match =
              !this.isWeekend(i, d) &&
              !this.isSpecialDay(i, d) &&
              !isCustom;
          } else if (type === "specialDay") {
            match = this.isSpecialDay(i, d) && !isCustom;
          }
          if (match) {
            if (!this.cellData[key]) this.cellData[key] = { price: "", inventory: this.cottageDetails?.cottage_count ?? 0 };
            this.cellData[key].price = price;
          }
        }
      }
    },
    allowNumbersOnly(e) {
      const val = e.target.value;
      e.target.value = val
        .replace(/[^0-9.]/g, "")
        .replace(/(\.\d*)\..*/, "$1");
    },
    deletePopupCustomPrice() {
      if (!this.popupCell) return;
      this.resetCellToBase(this.popupCell.monthIndex, this.popupCell.date);
      this.closeCellPopup();
    },
    deleteCellCustomPrice(monthIndex, date) {
      this.resetCellToBase(monthIndex, date);
    },
    removePreviewItem(item) {
      const [monthIndex, date] = item.key.split("-").map(Number);
      const key = this.cellKey(monthIndex, date);
      if (item.action === "base") {
        const saved = this.lastSavedCellData[key];
        if (saved) {
          const copy = JSON.parse(JSON.stringify(saved));
          if (this.$set) {
            this.$set(this.cellData, key, copy);
          } else {
            this.cellData[key] = copy;
          }
        }
        return;
      }
      this.resetCellToBase(monthIndex, date);
    },
    openPreviewModal() {
      this.showPreviewModal = true;
    },
    closePreviewModal() {
      this.showPreviewModal = false;
    },
    submitPriceUpdates() {
  
      const cottage = this.selectedCottage;
      if (!cottage) {
        this.closePreviewModal();
        return;
      }

      const items = [];

      const weekdayChanged =
        String(this.bulkPrices.weekday) !== String(this.originalBulkPrices.weekday);
      const weekendChanged =
        String(this.bulkPrices.weekend) !== String(this.originalBulkPrices.weekend);
      const specialDayChanged =
        String(this.bulkPrices.specialDay) !== String(this.originalBulkPrices.specialDay);
      if (weekdayChanged || weekendChanged || specialDayChanged) {
        const baseItem = { cottage_id: cottage._id };
        if (this.bulkPrices.weekday) {
          baseItem.weekdays_price = Number(this.bulkPrices.weekday);
        }
        if (this.bulkPrices.weekend) {
          baseItem.weekends_price = Number(this.bulkPrices.weekend);
        }
        if (this.bulkPrices.specialDay) {
          baseItem.special_days_price = Number(this.bulkPrices.specialDay);
        }
        if (
          baseItem.weekdays_price != null ||
          baseItem.weekends_price != null ||
          baseItem.special_days_price != null
        ) {
          items.push(baseItem);
        }
      }

      this.previewChanges.forEach((item) => {
        if (item.action === "base") return;
        const [monthIndex, day] = item.key.split("-").map(Number);
       
        const isoDate = `${this.year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const cellKey = this.cellKey(monthIndex, day);
        const cell = this.cellData[cellKey] || {};
        const isDelete = item.action === "delete";
        const dateItem = {
          cottage_id: cottage._id,
          date: isoDate,
        };
        if (isDelete) {
          // Deleted custom price
          dateItem.price = Number(item.newPrice || 0);
          dateItem.status = false;
        } else {
          dateItem.price = Number(item.newPrice || 0);
          dateItem.status = true;
        }
        items.push(dateItem);
      });

      if (!items.length) {
        this.closePreviewModal();
        return;
      }

      this.$apiV1
        .post("/farm-stay/cottage-price", { items })
        .then((res) => {
          if (res.data?.success) {
            this.lastSavedCellData = JSON.parse(JSON.stringify(this.cellData));
            this.originalBulkPrices = {
              weekend: this.bulkPrices.weekend,
              weekday: this.bulkPrices.weekday,
              specialDay: this.bulkPrices.specialDay,
            };
          }
        })
        .finally(() => {
          this.closePreviewModal();
        });
    },
  },
};
</script>

<style scoped>
.pricing-page {

  padding: 2rem;
}

.pricing-grid {
  font-size: 0.875rem;
}

.month-col {
  min-width: 100px;
  vertical-align: middle;
}

.date-col {
  min-width: 36px;
  max-width: 36px;
  font-weight: 600;
  font-size: 0.7rem;
}

.date-cell {
  padding: 2px !important;
  vertical-align: middle;
}

.cell-delete-btn {
  font-size: 0.7rem;
  line-height: 1;
}

.date-cell.weekend {
  background-color: #ffe4e6;
}

.date-cell.weekday {
  background-color: #f0f9ff;
}

.date-cell.special-day {
  background-color: #fef3c7;
}

.date-cell.custom-price {
  background-color: #d1fae5;
}

.date-cell.clickable {
  cursor: pointer;
}

.date-cell .day-label {
  font-size: 0.55rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1px;
}

.date-cell .cell-price {
  font-size: 0.65rem;
  font-weight: 600;
}

.price-input {
  width: 100%;
  min-width: 28px;
  max-width: 36px;
  padding: 2px 4px;
  font-size: 0.7rem;
  height: 24px;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.05);
}

.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.color-legend {
  font-size: 0.8rem;
  color: #495057;
}

.legend-label {
  color: #212529;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 1rem;
}

.legend-swatch {
  width: 16px;
  height: 16px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 3px;
}

.legend-swatch.weekend {
  background-color: #ffe4e6;
}

.legend-swatch.weekday {
  background-color: #f0f9ff;
}

.legend-swatch.special-day {
  background-color: #fef3c7;
}

.legend-swatch.custom-price {
  background-color: #d1fae5;
}
</style>
